import React, { useState } from 'react'
import { Tooltip, message } from 'antd'
import { LinkOutlined, LoadingOutlined } from '@ant-design/icons'
import adminRequest from 'utils/adminRequest'
import authRequest from 'utils/authRequest'

// Per-row "Copy Link" action. Fetches the SAME link the invite email would send
// for this candidate × assessment, and copies it to the clipboard so an admin
// can share it manually (WhatsApp/SMS) when email delivery fails.
//
// - Corporate (entityType === 'corporate'): the corporate OTP invite link
//   (admin-node GET /assessment/invite-link/:assessment_assigned_id). The real
//   assessment_assigned_id is record.assessmentAssignedId — record.id is the
//   candidate email (StudentsTable college & corporate rows both set id = email).
// - Institute (everything else): the student activation link, resolved by email
//   (user-management-node GET /user/invite-link?email=...) so it works even for
//   students who were never onboarded.
const CopyLinkButton = ({ record, entityType }) => {
  const [loading, setLoading] = useState(false)

  const writeToClipboard = async text => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
    // Fallback for non-secure contexts where the Clipboard API is unavailable.
    const el = document.createElement('textarea')
    el.value = text
    el.style.position = 'fixed'
    el.style.opacity = '0'
    document.body.appendChild(el)
    el.select()
    let ok = false
    try {
      ok = document.execCommand('copy')
    } catch (e) {
      ok = false
    }
    document.body.removeChild(el)
    return ok
  }

  const handleCopy = async () => {
    if (loading) return
    setLoading(true)
    try {
      let link = null
      if (entityType === 'corporate') {
        const assignedId = record.assessmentAssignedId || record.id
        if (!assignedId) {
          message.error('No assessment id on record to build the link')
          return
        }
        const res = await adminRequest.get(
          `/assessment/invite-link/${assignedId}`
        )
        link = res?.data?.data?.inviteUrl
      } else {
        if (!record.email) {
          message.error('No email on record to build the link')
          return
        }
        const res = await authRequest.get('/user/invite-link', {
          params: { email: record.email },
        })
        link = res?.data?.data?.activeLink
      }

      if (!link) {
        message.error('Could not generate the link')
        return
      }
      const copied = await writeToClipboard(link)
      if (copied) {
        message.success('Assessment link copied')
      } else {
        message.warning(`Copy failed. Link: ${link}`)
      }
    } catch (err) {
      const status = err?.response?.status
      if (status === 404) {
        message.error(
          err?.response?.data?.message ||
            'Student not yet onboarded. Send the invite first.'
        )
      } else {
        message.error('Could not copy the link. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Tooltip title="Copy assessment link to share">
      <span
        role="button"
        onClick={handleCopy}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: loading ? 'default' : 'pointer',
          color: '#3249D7',
          fontWeight: 600,
          fontSize: 13,
          whiteSpace: 'nowrap',
        }}
      >
        {loading ? <LoadingOutlined /> : <LinkOutlined />}
        Copy Link
      </span>
    </Tooltip>
  )
}

export default CopyLinkButton
